import type { SupportedLanguageCode } from '@/features/language/store';

export type MockPortalRole = 'admin' | 'user' | 'partner';

export interface MockAssociatedCompany {
  id: string;
  name: string;
  relationship: 'Internal' | 'Customer' | 'Partner';
  industry: string;
  website: string;
  supportTier: 'Priority' | 'Standard' | 'Strategic';
}

export interface MockContactRecord {
  entityType: 'Internal Contact' | 'Customer Contact' | 'Partner Contact';
  jobTitle: string;
  department: string;
  lifecycleStage: 'Employee' | 'Customer' | 'Partner';
  portalStatus: 'Active' | 'Invited';
  preferredLanguage: SupportedLanguageCode;
  lastSyncAt: string;
}

export interface MockUser {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: MockPortalRole;
  associatedCompany: MockAssociatedCompany | null;
  contactRecord: MockContactRecord | null;
  settings: {
    theme: 'light' | 'dark' | 'system';
    notifications: boolean;
    language: SupportedLanguageCode;
  };
}

const mockUsers: Record<MockPortalRole, MockUser> = {
  admin: {
    id: 'u-123',
    email: 'admin@wianco.example',
    name: 'Alex Admin',
    avatar: 'https://ui-avatars.com/api/?name=Alex+Admin&background=0D8ABC&color=fff',
    role: 'admin',
    associatedCompany: {
      id: 'cmp-wianco',
      name: 'wianco',
      relationship: 'Internal',
      industry: 'Digital Services',
      website: 'https://www.wianco.com',
      supportTier: 'Priority',
    },
    contactRecord: {
      entityType: 'Internal Contact',
      jobTitle: 'Platform Administrator',
      department: 'Operations',
      lifecycleStage: 'Employee',
      portalStatus: 'Active',
      preferredLanguage: 'en',
      lastSyncAt: '2026-03-30T08:15:00.000Z',
    },
    settings: {
      theme: 'system',
      notifications: true,
      language: 'en',
    },
  },
  user: {
    id: 'u-456',
    email: 'maria.keller@fabrikam.example',
    name: 'Maria Keller',
    avatar: 'https://ui-avatars.com/api/?name=Maria+Keller&background=0D8ABC&color=fff',
    role: 'user',
    associatedCompany: {
      id: 'cmp-fabrikam',
      name: 'Fabrikam Foods GmbH',
      relationship: 'Customer',
      industry: 'Food Manufacturing',
      website: 'https://fabrikam.example',
      supportTier: 'Standard',
    },
    contactRecord: {
      entityType: 'Customer Contact',
      jobTitle: 'Operations Manager',
      department: 'Manufacturing',
      lifecycleStage: 'Customer',
      portalStatus: 'Active',
      preferredLanguage: 'de',
      lastSyncAt: '2026-03-29T15:40:00.000Z',
    },
    settings: {
      theme: 'system',
      notifications: true,
      language: 'de',
    },
  },
  partner: {
    id: 'u-789',
    email: 'luca.weber@northwind.example',
    name: 'Luca Weber',
    avatar: 'https://ui-avatars.com/api/?name=Luca+Weber&background=0D8ABC&color=fff',
    role: 'partner',
    associatedCompany: {
      id: 'cmp-northwind',
      name: 'Northwind Systems GmbH',
      relationship: 'Partner',
      industry: 'Industrial Automation',
      website: 'https://northwind.example',
      supportTier: 'Strategic',
    },
    contactRecord: {
      entityType: 'Partner Contact',
      jobTitle: 'Alliance Lead',
      department: 'Partner Success',
      lifecycleStage: 'Partner',
      portalStatus: 'Active',
      preferredLanguage: 'en',
      lastSyncAt: '2026-03-30T06:25:00.000Z',
    },
    settings: {
      theme: 'system',
      notifications: true,
      language: 'en',
    },
  },
};

const resolveMockRole = (): MockPortalRole => {
  const requestedRole = import.meta.env.VITE_MOCK_PROFILE_ROLE?.toLowerCase();

  if (requestedRole === 'user' || requestedRole === 'partner' || requestedRole === 'admin') {
    return requestedRole;
  }

  return 'admin';
};

export const mockUser: MockUser = mockUsers[resolveMockRole()];
